import { useCallback, useState } from 'react';
// Hook
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(() => {
        if (typeof window === 'undefined') {
            return initialValue;
        }
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            // If error also return initialValue
            console.error(error);
            return initialValue;
        }
    });

    const store = useCallback(
        (value: any) => {
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(key, JSON.stringify(value));
            }
        },
        [key]
    );

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = useCallback(
        (value: T | ((val: T) => T)): void => {
            try {
                // Allow value to be a function so we have same API as useState
                if (value instanceof Function) {
                    setStoredValue((prev) => {
                        const updated = value(prev);
                        store(updated);
                        return updated;
                    });
                } else {
                    store(value);
                    setStoredValue(value);
                }
            } catch (error) {
                // A more advanced implementation would handle the error case
                console.error(error);
            }
        },
        [store, setStoredValue]
    );
    return [storedValue, setValue];
}
